import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {ColorPicker} from "primereact/colorpicker";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	RedactAnnotation,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import RedactDialogFields from "../../field/redact/RedactDialogFields";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class RedactEditDialogDefinition extends PortalDialog<ToolboxAnnotationAnnotation,
	PortalDialogProperties<ToolboxAnnotationAnnotation>,
	PortalDialogState<ToolboxAnnotationAnnotation>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<RedactDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.redact, Functions.getRedactDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.REDACT_AREA, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let errors: Map<RedactDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.edit?.redact?.position?.pathElement)) {
			return null;
		}

		let redactParameter: RedactAnnotation = parameter!.edit!.redact!;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.CREATOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-creator">
							<Translate {...this.props} data="redact.content.creator.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-creator"
									   className={errors.has(RedactDialogFields.CREATOR) ? "p-invalid" : ""}
									   tooltip={errors.has(RedactDialogFields.CREATOR) ? Translate.value(
										   errors.get(RedactDialogFields.CREATOR)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   redactParameter?.creator, RedactAnnotation.getCreatorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   RedactDialogFields.CREATOR, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(RedactDialogFields.CREATOR) ? Translate.value(
								errors.get(RedactDialogFields.CREATOR)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.SUBJECT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-subject">
							<Translate {...this.props} data="redact.content.subject.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-subject"
									   value={FunctionHelper.applyValue(
										   redactParameter?.subject, RedactAnnotation.getSubjectDefault()
									   )}
									   onInput={(e) => this.onChange(
										   RedactDialogFields.SUBJECT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.CONTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-text">
							<Translate {...this.props} data="redact.content.text.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputTextarea id="control-text" rows={2}
										   value={FunctionHelper.applyValue(
											   redactParameter?.contents, RedactAnnotation.getContentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.CONTENTS, e.currentTarget.value
										   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.INTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-intents">
							<Translate {...this.props} data="redact.content.intents" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-intents"
									   value={FunctionHelper.applyValue(
										   redactParameter?.intents, RedactAnnotation.getIntentsDefault()
									   )}
									   onInput={(e) => this.onChange(
										   RedactDialogFields.INTENTS, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.BORDER)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-color-outline">
							<Translate {...this.props} data="redact.appearance.color.outline" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(RedactDialogFields.BORDER) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(RedactDialogFields.BORDER) ? "p-invalid" : ""}
									tooltip={errors.has(RedactDialogFields.BORDER) ? Translate.value(
										errors.get(RedactDialogFields.BORDER)!, {ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										redactParameter?.color, RedactAnnotation.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										RedactDialogFields.BORDER, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-color-outline" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 RedactAnnotation.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 redactParameter?.color, RedactAnnotation.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.BORDER, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(RedactDialogFields.BORDER) ? Translate.value(
								errors.get(RedactDialogFields.BORDER)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.FILL)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-color-interior">
							<Translate {...this.props} data="redact.appearance.color.interior" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(RedactDialogFields.FILL) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(RedactDialogFields.FILL) ? "p-invalid" : ""}
										   tooltip={errors.has(RedactDialogFields.FILL) ? Translate.value(
											   errors.get(RedactDialogFields.FILL)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   redactParameter?.interiorColor,
											   RedactAnnotation.getInteriorColorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.FILL, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-color-interior" className="p-inputgroup-addon"
											 format="hex"
											 defaultColor={RedactAnnotation.getInteriorColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 redactParameter?.interiorColor,
												 RedactAnnotation.getInteriorColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.FILL, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(RedactDialogFields.FILL) ? Translate.value(
								errors.get(RedactDialogFields.FILL)!, {ns: "message"}
							) : "")}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["redact-edit-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.redact.area.edit"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.redact.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: RedactDialogFields, value: any): void {
		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let redactParameter: RedactAnnotation = parameter.edit!.redact!;
		let errors: Map<RedactDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case RedactDialogFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				redactParameter.creator = value;
				break;
			case RedactDialogFields.SUBJECT:
				redactParameter.subject = value;
				break;
			case RedactDialogFields.CONTENTS:
				redactParameter.contents = value;
				break;
			case RedactDialogFields.INTENTS:
				redactParameter.intents = value;
				break;
			case RedactDialogFields.FILL:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				redactParameter.interiorColor = value;
				break;
			case RedactDialogFields.BORDER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				redactParameter.color = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-edit"}/>
					<Translate {...this.props} data="redact.edit" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			annotation: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.redact.edit", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.REDACT_EDIT;
	}

	protected getDefaultParameter(): Parameter<ToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const RedactEditDialog = React.memo(RedactEditDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RedactEditDialog;